/**
 * Internal Functions - handled by interpreter, not providers
 * Includes: timer, state machine, control flow, conversions, string ops, file I/O
 */
import { SystemFunction, ValueType } from '@emdzej/inpax-core';
import { Stack } from '../vm/stack.js';
import { getLogger } from '@emdzej/bimmerz-logger';
const log = getLogger('INPAX.internal-functions');
export class InternalFunctions {
    vm;
    handlers = new Map();
    timers = new Map();
    fileHandle = null;
    fileLineIndex = 0;
    constructor(vm) {
        this.vm = vm;
        this.registerHandlers();
    }
    registerHandlers() {
        // Timer
        this.handlers.set(SystemFunction.settimer, (ctx) => this.settimer(ctx));
        this.handlers.set(SystemFunction.testtimer, (ctx) => this.testtimer(ctx));
        // Control
        this.handlers.set(SystemFunction.exit, () => this.exit());
        this.handlers.set(SystemFunction.exitwindows, () => this.exit());
        this.handlers.set(SystemFunction.delay, (ctx) => this.delay(ctx));
        // Time
        this.handlers.set(SystemFunction.getdate, (ctx) => this.getdate(ctx));
        this.handlers.set(SystemFunction.gettime, (ctx) => this.gettime(ctx));
        // String
        this.handlers.set(SystemFunction.strcat, (ctx) => this.strcat(ctx));
        this.handlers.set(SystemFunction.strlen, (ctx) => this.strlen(ctx));
        this.handlers.set(SystemFunction.midstr, (ctx) => this.midstr(ctx));
        // Conversion
        this.handlers.set(SystemFunction.realtostring, (ctx) => this.realtostring(ctx));
        this.handlers.set(SystemFunction.stringtoreal, (ctx) => this.stringtoreal(ctx));
        this.handlers.set(SystemFunction.inttostring, (ctx) => this.inttostring(ctx));
        this.handlers.set(SystemFunction.stringtoint, (ctx) => this.stringtoint(ctx));
        this.handlers.set(SystemFunction.hexconvert, (ctx) => this.hexconvert(ctx));
        this.handlers.set(SystemFunction.realtoint, (ctx) => this.realtoint(ctx));
        this.handlers.set(SystemFunction.inttoreal, (ctx) => this.inttoreal(ctx));
        this.handlers.set(SystemFunction.bytetoint, (ctx) => this.bytetoint(ctx));
        this.handlers.set(SystemFunction.inttolong, (ctx) => this.inttolong(ctx));
        this.handlers.set(SystemFunction.longtoreal, (ctx) => this.longtoreal(ctx));
        // File I/O
        this.handlers.set(SystemFunction.fileopen, (ctx) => this.fileopen(ctx));
        this.handlers.set(SystemFunction.fileclose, () => this.fileclose());
        this.handlers.set(SystemFunction.filewrite, (ctx) => this.filewrite(ctx));
        this.handlers.set(SystemFunction.fileread, (ctx) => this.fileread(ctx));
        // Binary
        this.handlers.set(SystemFunction.GetBinaryDataString, (ctx) => this.getBinaryDataString(ctx));
        // Menu
        this.handlers.set(SystemFunction.setmenu, (ctx) => this.setmenu(ctx));
        // Screen
        this.handlers.set(SystemFunction.setscreen, (ctx) => this.setscreen(ctx));
        // State Machine
        this.handlers.set(SystemFunction.setstatemachine, (ctx) => this.setstatemachine(ctx));
        this.handlers.set(SystemFunction.setstate, (ctx) => this.setstate(ctx));
        this.handlers.set(SystemFunction.callstatemachine, (ctx) => this.callstatemachine(ctx));
        this.handlers.set(SystemFunction.returnstatemachine, () => this.returnstatemachine());
        // Job Control (stubs)
        this.handlers.set(SystemFunction.setjobstatus, () => this.stub('setjobstatus'));
        this.handlers.set(SystemFunction.scriptselect, (ctx) => this.scriptselect(ctx));
        this.handlers.set(SystemFunction.scriptchange, () => this.stub('scriptchange'));
        // INPA's STEUERN (control/activation) session verbs. Mirrors the
        // `DAT_004a0008` state machine in INPA.exe; see
        // `runtime/control-session.ts` for the field-by-field anchor.
        //
        //   select(MultipleSelectFlag) — async picker; lives in the
        //     dispatcher (`packages/dispatcher/src/dispatcher.ts`) because
        //     it awaits a UI provider call. Not registered here.
        //   deselect / start / stop — synchronous state mutations on the
        //     VM's `ControlSession`.
        //   control — only used by 8 legacy E36/E38 IPOs (LCM/LSZ/ZKE2
        //     lighting era); leave as a no-op until we find a real caller.
        this.handlers.set(SystemFunction.deselect, () => this.vm.getControlSession().deselect());
        this.handlers.set(SystemFunction.control, () => { });
        this.handlers.set(SystemFunction.start, () => {
            const items = this.vm.getScreenExecutor()?.getToggleItems() ?? [];
            this.vm.getControlSession().start(items);
        });
        this.handlers.set(SystemFunction.stop, () => this.vm.getControlSession().stop());
        this.handlers.set(SystemFunction.getapistring, (ctx) => this.getapistring(ctx));
        // togglelist is dispatched through the UI provider (`ui.togglelist`)
        // — it opens an async multi-select dialog rather than running
        // internally. See `packages/dispatcher/src/dispatcher.ts`.
        // String Arrays (stubs)
        this.handlers.set(SystemFunction.StrArrayCreate, () => this.stub('StrArrayCreate'));
        this.handlers.set(SystemFunction.StrArrayDestroy, () => this.stub('StrArrayDestroy'));
        this.handlers.set(SystemFunction.StrArrayWrite, () => this.stub('StrArrayWrite'));
        this.handlers.set(SystemFunction.StrArrayRead, () => this.stub('StrArrayRead'));
        this.handlers.set(SystemFunction.StrArrayGetElementCount, () => this.stub('StrArrayGetElementCount'));
        this.handlers.set(SystemFunction.StrArrayDelete, () => this.stub('StrArrayDelete'));
        // Structures (stubs)
        this.handlers.set(SystemFunction.CreateStructure, () => this.stub('CreateStructure'));
        this.handlers.set(SystemFunction.SetStructureMode, () => this.stub('SetStructureMode'));
        this.handlers.set(SystemFunction.StructureByte, () => this.stub('StructureByte'));
        this.handlers.set(SystemFunction.StructureInt, () => this.stub('StructureInt'));
        this.handlers.set(SystemFunction.StructureLong, () => this.stub('StructureLong'));
        this.handlers.set(SystemFunction.StructureString, () => this.stub('StructureString'));
    }
    call(funcId, ctx) {
        const handler = this.handlers.get(funcId);
        if (!handler) {
            log.warn({ funcId: `0x${funcId.toString(16)}` }, 'internal function not implemented');
            return;
        }
        handler(ctx);
    }
    stub(name) {
        log.warn({ name }, 'stub function not implemented');
    }
    /**
     * `getapistring(argNumFlag, fullScreenFlag, out: string ApiString)` —
     * reads back whichever argument INPA's external launcher (if any)
     * passed when starting the script.
     *
     * ## Why this exists in real INPA
     *
     * INPA.exe runs in two modes:
     *
     *   1. **Interactive** — the user double-clicks an INPA shortcut or
     *      picks an entry from the on-screen menus. No launcher arg.
     *   2. **API-driven** — another Windows program (typically a BMW
     *      diagnostic tool like ISTA-D's predecessor) launches INPA as
     *      a controlled child via its own apiInit-style entry points.
     *      INPA exposes these alongside the EDIABAS ones — Ghidra's
     *      import table on `INPA.EXE` shows `___apiInit@4`,
     *      `___apiInitExt@20`, `___apiJob@20`, `___apiResultText@20`,
     *      etc., mirroring the EDIABAS API surface (api32.DLL). The
     *      launcher passes an arg STRING and an arg NUMBER through
     *      those entry points; the IPO script reads them back via
     *      `getapistring`.
     *
     * The canonical signature is in the INPA SDK header (`ref/Inpa.h:37`):
     *
     *     extern getapistring( in: bool ArgNumFlag,
     *                          in: bool FullScreenFlag,
     *                          out: string ApiString );
     *
     *   - `ArgNumFlag = true`  → return the numeric variant of the
     *     launcher arg (formatted as a decimal string).
     *   - `ArgNumFlag = false` → return the string variant.
     *   - `FullScreenFlag`     → INPA's render-mode hint; orthogonal to
     *     the value being read.
     *
     * ## How MS43 uses it (typical pattern)
     *
     * `s_digital1`'s INIT block does:
     *
     *     getapistring(false, false, → GLOBAL[32])
     *     INPAapiJob(handle, "STATUS_DIGITAL", GLOBAL[32], "")
     *
     * So the launcher's arg becomes the **EDIABAS job argument** for
     * `STATUS_DIGITAL` — letting an external tool pre-filter the SGBD
     * result set without touching the script. With no launcher (which
     * is the case in inpax-web and the CLI), there's no arg → empty
     * string → `STATUS_DIGITAL` runs with no filter and returns the
     * full result set, which is exactly what the LINE blocks below
     * expect when displaying every status flag.
     *
     * ## Why we return `""`
     *
     * We don't (yet) implement INPA's own apiInit surface, so there's
     * no launcher to pass an arg in the first place. Returning `""`
     * matches the interactive case in real INPA and lets all the
     * standard INPA scripts work unchanged. If we later add a way to
     * launch IPOs with a pre-set arg (CLI flag, URL hash), this is
     * the hook to read it back from.
     *
     * `argNumFlag` and `fullScreenFlag` are accepted but ignored — we
     * always resolve the string variant as `""`, and the script's
     * subsequent `INPAapiJob` happily accepts an empty job arg.
     *
     * ## Stack discipline
     *
     * Stack at call time, after `FRAME`, in push order (matches the
     * disassembly):
     *
     *     [argNumFlag: bool, fullScreenFlag: bool, ApiString: ref]
     *
     * We pop LIFO so reads happen in reverse declaration order, then
     * write `""` through the out ref via `setOutParam`.
     */
    getapistring(ctx) {
        const outRef = ctx.popRef();
        ctx.popBool(); // fullScreenFlag — ignored
        ctx.popBool(); // argNumFlag — ignored
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, ''));
    }
    // ============ Script Control ============
    /**
     * `scriptselect(iniFile)` — INPA's "switch to another script" entry
     * point. The script passes a filename like `"E46.ENG"` (built from
     * a configured F-key binding + language suffix); the host shows a
     * picker driven by that INI's section tree + entries, the user
     * confirms an IPO, and the host swaps the currently-running script.
     *
     * Fire-and-forget by design: we kick off `ui.scriptSelect()` (which
     * returns a Promise resolving to the chosen IPO basename or null
     * for cancel) and let the calling bytecode continue. If the user
     * confirms, the host's `script:switch` listener tears down the
     * runtime and replaces it, so the continuing instructions become
     * moot. If the user cancels, nothing happens and the script keeps
     * going. Either way, the caller doesn't see a return value — the
     * INPA signature has none.
     */
    scriptselect(ctx) {
        const iniFile = ctx.popString();
        const ui = this.vm.getRuntime().ui;
        void ui
            .scriptSelect(iniFile)
            .then((ipo) => {
            if (ipo) {
                ui.emit('script:switch', { ipo, iniFile });
            }
        })
            .catch((err) => {
            log.error({ err, iniFile }, 'scriptselect failed');
        });
    }
    // ============ Timer ============
    settimer(ctx) {
        const timeval = ctx.popInt();
        const timernum = ctx.popInt();
        const executor = this.vm.getScreenExecutor();
        if (executor) {
            executor.setTimer(timernum, timeval);
        }
        else {
            this.timers.set(timernum, { start: Date.now(), duration: timeval });
        }
    }
    testtimer(ctx) {
        const outRef = ctx.popRef();
        const timernum = ctx.popInt();
        const executor = this.vm.getScreenExecutor();
        let expired;
        if (executor) {
            expired = executor.testTimer(timernum);
        }
        else {
            const timer = this.timers.get(timernum);
            expired = timer ? (Date.now() - timer.start >= timer.duration) : true;
        }
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Bool, expired));
    }
    // ============ Menu ==============
    setmenu(ctx) {
        const menuHandle = ctx.popInt();
        this.vm.setMenu(menuHandle).catch(err => {
            log.error({ err }, 'setmenu error activating menu');
        });
    }
    // ============ Screen ============
    setscreen(ctx) {
        const cyclic = ctx.popInt() !== 0;
        const handle = ctx.popRef();
        const screenId = handle.refInfo?.index;
        if (screenId === undefined) {
            log.warn('setscreen invalid screen reference');
            return;
        }
        const screenName = this.findScreenByHandle(screenId);
        if (!screenName) {
            log.warn({ screenId }, 'setscreen screen not found for handle');
            return;
        }
        // `vm.setScreen` builds a fresh `ScreenExecutor` for the new SCREEN
        // and `await`s `.start()`. `start()` is itself the canonical place
        // that calls `ui.setScreen(handle, cyclic)` + `ui.setTotalLines(N)`
        // — see `screen-executor.ts:start`. We used to call `ui.setScreen`
        // here too as a sync-side "first paint" cue, but that fired AFTER
        // the awaited `start()` resolved, which had the effect of wiping
        // the pagination state the executor had just set (totalLines went
        // 7 → 0 mid-cycle, and `ScrollIndicator` saw the wrong window).
        // Trust the executor: it sets the screen + totals atomically.
        this.vm.setScreen(screenId, cyclic).catch(err => {
            log.error({ err, screenId }, 'setscreen error activating screen');
        });
    }
    findScreenByHandle(handle) {
        const ipo = this.vm.getIpo();
        if (!ipo?.screens)
            return null;
        const screen = ipo.screens.get(handle);
        return screen?.header?.name ?? null;
    }
    // ============ State Machine ============
    setstatemachine(ctx) {
        const handle = ctx.popInt();
        const smExecutor = this.vm.getStateMachineExecutor();
        if (!smExecutor) {
            log.warn('setstatemachine no state machine executor');
            return;
        }
        const sm = this.findStateMachineByHandle(handle);
        if (sm) {
            smExecutor.start(sm);
        }
        else {
            log.warn({ handle }, 'setstatemachine state machine not found for handle');
        }
    }
    setstate(ctx) {
        const handle = ctx.popInt();
        const smExecutor = this.vm.getStateMachineExecutor();
        if (!smExecutor) {
            log.warn('setstate no state machine executor');
            return;
        }
        const stateName = this.findStateNameByHandle(handle);
        if (stateName) {
            smExecutor.setState(stateName);
        }
        else {
            log.warn({ handle }, 'setstate state not found for handle');
        }
    }
    callstatemachine(ctx) {
        const handle = ctx.popInt();
        const smExecutor = this.vm.getStateMachineExecutor();
        if (!smExecutor) {
            log.warn('callstatemachine no state machine executor');
            return;
        }
        const sm = this.findStateMachineByHandle(handle);
        if (sm) {
            smExecutor.callStateMachine(sm);
        }
        else {
            log.warn({ handle }, 'callstatemachine state machine not found for handle');
        }
    }
    returnstatemachine() {
        const smExecutor = this.vm.getStateMachineExecutor();
        if (!smExecutor) {
            log.warn('returnstatemachine no state machine executor');
            return;
        }
        smExecutor.returnStateMachine();
    }
    findStateMachineByHandle(handle) {
        const ipo = this.vm.getIpo();
        if (!ipo?.stateMachines)
            return null;
        const sm = ipo.stateMachines.get(handle);
        return sm?.header?.name ?? null;
    }
    findStateNameByHandle(handle) {
        const ipo = this.vm.getIpo();
        if (!ipo?.stateMachines)
            return null;
        for (const sm of ipo.stateMachines.values()) {
            if (sm.func?.header?.blockId === handle) {
                return 'INIT';
            }
            for (const state of sm.states) {
                if (state.header.blockId === handle) {
                    return state.header.name;
                }
            }
        }
        return null;
    }
    // ============ Control ============
    exit() {
        // Stop the VM (scheduler + screen / state-machine executors) and
        // signal the host so it can dispose the runtime handle and return
        // to its idle "pick a script" state. Real INPA exits the entire
        // process; for inpax-web the script just unloads.
        this.vm.stop();
        try {
            this.vm.getRuntime().ui.emit('script:exit');
        }
        catch (err) {
            // Don't let a flaky listener crash the exit path.
            log.warn({ err }, 'script:exit listener threw');
        }
    }
    delay(ctx) {
        const ms = ctx.popInt();
        const start = Date.now();
        while (Date.now() - start < ms) { /* busy wait */ }
    }
    // ============ Time ============
    getdate(ctx) {
        const outRef = ctx.popRef();
        const now = new Date();
        const date = `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()}`;
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, date));
    }
    gettime(ctx) {
        const outRef = ctx.popRef();
        const now = new Date();
        const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, time));
    }
    // ============ String ============
    strcat(ctx) {
        const right = ctx.popString();
        const left = ctx.popString();
        const outRef = ctx.popRef();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, left + right));
    }
    strlen(ctx) {
        const str = ctx.popString();
        const outRef = ctx.popRef();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Int, str.length));
    }
    midstr(ctx) {
        const length = ctx.popInt();
        const start = ctx.popInt();
        const str = ctx.popString();
        const outRef = ctx.popRef();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, str.substr(start, length)));
    }
    // ============ Conversion ============
    realtostring(ctx) {
        const outRef = ctx.popRef();
        ctx.popString(); // format string - TODO: implement formatting
        const value = ctx.popReal();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, value.toString()));
    }
    stringtoreal(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popString();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Real, parseFloat(value) || 0));
    }
    inttostring(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popInt();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.String, value.toString()));
    }
    stringtoint(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popString();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Int, parseInt(value, 10) || 0));
    }
    hexconvert(ctx) {
        const segRef = ctx.popRef();
        const lowRef = ctx.popRef();
        const midRef = ctx.popRef();
        const highRef = ctx.popRef();
        const hexStr = ctx.popString();
        const clean = hexStr.replace(/[^0-9A-Fa-f]/g, '');
        const num = parseInt(clean, 16) || 0;
        ctx.setOutParam(highRef, Stack.createEntry(ValueType.Int, (num >> 24) & 0xFF));
        ctx.setOutParam(midRef, Stack.createEntry(ValueType.Int, (num >> 16) & 0xFF));
        ctx.setOutParam(lowRef, Stack.createEntry(ValueType.Int, (num >> 8) & 0xFF));
        ctx.setOutParam(segRef, Stack.createEntry(ValueType.Int, num & 0xFF));
    }
    realtoint(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popReal();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Int, Math.floor(value)));
    }
    inttoreal(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popInt();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Real, value));
    }
    bytetoint(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popInt() & 0xFF;
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Int, value));
    }
    inttolong(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popInt();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Long, value));
    }
    longtoreal(ctx) {
        const outRef = ctx.popRef();
        const value = ctx.popInt();
        ctx.setOutParam(outRef, Stack.createEntry(ValueType.Real, value));
    }
    // ============ File I/O ============
    fileopen(ctx) {
        const mode = ctx.popString();
        const path = ctx.popString();
        this.fileHandle = { path, mode, lines: [] };
        this.fileLineIndex = 0;
        log.warn({ path, mode }, 'file I/O not fully implemented');
    }
    fileclose() {
        this.fileHandle = null;
    }
    filewrite(ctx) {
        const str = ctx.popString();
        if (this.fileHandle) {
            this.fileHandle.lines.push(str);
        }
    }
    fileread(ctx) {
        const eofRef = ctx.popRef();
        const strRef = ctx.popRef();
        if (this.fileHandle && this.fileLineIndex < this.fileHandle.lines.length) {
            ctx.setOutParam(strRef, Stack.createEntry(ValueType.String, this.fileHandle.lines[this.fileLineIndex++]));
            ctx.setOutParam(eofRef, Stack.createEntry(ValueType.Bool, false));
        }
        else {
            ctx.setOutParam(strRef, Stack.createEntry(ValueType.String, ''));
            ctx.setOutParam(eofRef, Stack.createEntry(ValueType.Bool, true));
        }
    }
    // ============ Binary ============
    getBinaryDataString(ctx) {
        const lenRef = ctx.popRef();
        const strRef = ctx.popRef();
        ctx.setOutParam(strRef, Stack.createEntry(ValueType.String, ''));
        ctx.setOutParam(lenRef, Stack.createEntry(ValueType.Int, 0));
    }
}
//# sourceMappingURL=internal-functions.js.map